import React, { memo, useCallback } from 'react';
import { EdgeProps, BaseEdge, EdgeLabelRenderer, getBezierPath } from '@xyflow/react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRightIcon, PlayIcon, Loader2Icon } from 'lucide-react';
import { TopicEdge, getEdgeStatusColor } from '@/types/edgeTypes';
import { useMapState, useMapExtraction, useMapUI } from '@/contexts/map';

/**
 * CustomEdge component
 */
const CustomEdge = ({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style = {},
  markerEnd,
  selected,
}: EdgeProps) => {
  const edgeData = data as unknown as TopicEdge;
  const isActive = edgeData?.active;
  const isProcessing = edgeData?.status === 'processing';
  
  // 抽出処理のためのフック
  const { mapData, updateEdge } = useMapState();
  const { extractEdgeContent } = useMapExtraction();
  const { transcript } = useMapUI();
  
  // 個別抽出ボタンのクリックハンドラー
  const handleExtract = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // エッジ選択イベントを防止
    
    if (!mapData || !transcript || isProcessing) return;
    
    // 対応するエッジを取得
    const edge = mapData.edges.find(e => e.id === edgeData.id);
    if (!edge) return;
    
    // ソースとターゲットのノードのアクティブ状態を取得
    const sourceNode = mapData.nodes.find(n => n.id === edge.source);
    const targetNode = mapData.nodes.find(n => n.id === edge.target);
    
    if (!sourceNode || !targetNode) return;
    
    // 抽出処理
    extractEdgeContent(edge, sourceNode.active, targetNode.active, transcript)
      .then(updatedEdge => {
        if (updatedEdge) {
          updateEdge(updatedEdge.id, updatedEdge);
        }
      })
      .catch(error => {
        console.error('Edge extraction error:', error);
      });
  }, [mapData, transcript, edgeData, isProcessing, extractEdgeContent, updateEdge]);
  
  // Default edge style
  const defaultStyle = {
    strokeWidth: isActive ? 2 : 1,
    stroke: getEdgeStatusColor(edgeData?.status || 'pending'),
    strokeDasharray: isActive ? 'none' : '5,5', // Dashed line for inactive edges
    opacity: isActive ? 1 : 0.4, // Reduced opacity for inactive edges
  };
  
  // Selected edge style
  const selectedStyle = {
    ...defaultStyle,
    strokeWidth: defaultStyle.strokeWidth + 1,
    filter: 'drop-shadow(0 0 3px rgba(59, 130, 246, 0.5))', // Blue glow effect
    cursor: 'pointer', // Show pointer cursor for edges
  };
  
  // Combine default style with provided style and selected state
  const edgeStyle = {
    ...(selected ? selectedStyle : defaultStyle),
    ...style,
  };
  
  // Calculate the center point of the edge for the label
  const centerX = sourceX + (targetX - sourceX) * 0.5;
  const centerY = sourceY + (targetY - sourceY) * 0.5;
  
  // Increase curvature for more pronounced curves
  const curvature = 0.4;
  
  // Get content to display
  const displayContent = (): string => {
    if (edgeData.status === 'processing') {
      return '処理中...';
    }
    
    if (edgeData.contents && edgeData.contents.length > 0) {
      return edgeData.contents[0].content;
    }
    
    if (edgeData.content) {
      return edgeData.content;
    }
    
    return '内容を抽出中...';
  };
  
  return (
    <>
      {/* getBezierPath returns [path, labelX, labelY, offsetX, offsetY] */}
      <BaseEdge
        id={id}
        path={getBezierPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
          curvature: curvature // Increased curvature for more pronounced curves
        })[0]}
        style={edgeStyle}
        markerEnd={markerEnd}
        className="react-flow__edge-path-selector" // Add class for easier selection
      />
      
      {edgeData && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${centerX}px, ${centerY}px)`,
              pointerEvents: 'all',
              opacity: isActive ? 1 : 0.4, // Reduced opacity for inactive edges
              cursor: 'pointer', // Show pointer cursor
            }}
          >
            <HoverCard>
              <HoverCardTrigger asChild>
                <div className="flex items-center gap-1">
                  <Badge 
                    variant="outline" 
                    className={`cursor-pointer transition-colors ${selected ? 'bg-primary/10 border-primary' : ''}`}
                  >
                    <ArrowRightIcon className="h-3 w-3 mr-1" />
                    {isActive 
                      ? edgeData.status === 'success' ? '関連あり' : '確認中'
                      : '関連なし'}
                  </Badge>
                  
                  {/* 抽出ボタン - エッジの場合は小さめに */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleExtract}
                    disabled={isProcessing}
                    className="h-5 w-5 p-0 bg-background/80 hover:bg-background rounded-full"
                  >
                    {isProcessing ? (
                      <Loader2Icon className="h-3 w-3 animate-spin" />
                    ) : (
                      <PlayIcon className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              </HoverCardTrigger>
              <HoverCardContent className="w-64">
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">{edgeData.question}</h4>
                  {isActive ? (
                    <p className="text-xs">{displayContent()}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      接続されたトピックが非アクティブか、関連性が見つかりませんでした
                    </p>
                  )}
                  {selected && (
                    <p className="text-xs text-primary mt-1">
                      クリックして詳細を表示
                    </p>
                  )}
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default memo(CustomEdge);